/**
 * Internal session-replay viewer (dev only).
 *
 * This screen is the "pick a session" half — find a user or a day, see what was
 * recorded, errors first. Opening one hands off to ReplayEngine, which is the
 * full-bleed replay/debugging surface.
 *
 * Access is dev-only in three independent places, on purpose:
 *   1. the sidebar hides the link unless userDetails.isDev (Sidebar.js)
 *   2. ReplayAccessGate requires a second factor before this renders at all
 *   3. every endpoint it calls re-checks server-side (SessionReplay.py)
 * Only (2) and (3) are real controls — the sidebar rule just keeps it out of the
 * way of people who have no business here.
 */

import { useCallback, useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from 'reactstrap';
import Header from 'components/Headers/Header';
import { GlobalContext } from '../api/GlobalContext';
import ReplayDayViewer from './replay/ReplayDayViewer';
import ReplayAccessGate from './replay/ReplayAccessGate';
import { formatReplayTimestamp, localDateInputValue } from './replay/replayTime';
import { replayTimestamp } from './replay/replayDay';
import { readReplayLink } from './replay/replayLink';

const formatDuration = seconds => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours ? `${hours}h ` : ''}${minutes ? `${minutes}m ` : ''}${seconds % 60}s`;
};

const replayDuration = replay => {
  const start = replayTimestamp(replay.startedAt);
  const end = replayTimestamp(replay.endedAt);
  // endedAt is refreshed by every chunk upload, so it is "when we last heard
  // from this recording" — within one chunk interval of when it really stopped,
  // and present even on the replays that never finalized, which is most of them.
  if (start === null || end === null || end < start) return 'Unknown';
  return formatDuration(Math.round((end - start) / 1000));
};

const SessionReplayViewer = () => {
  const { apiClient } = useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [replayId, setReplayId] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [day, setDay] = useState(localDateInputValue());
  const [replays, setReplays] = useState([]);
  const [status, setStatus] = useState('');
  const [openReplay, setOpenReplay] = useState(() => readReplayLink(location.search));

  useEffect(() => {
    const linked = readReplayLink(location.search);
    const params = new URLSearchParams(location.search);
    setOpenReplay(linked);
    setStatus(!linked && (params.has('replayId') || params.has('at')) ? 'This replay link is invalid.' : '');
  }, [location.search]);

  const search = useCallback(async () => {
    setStatus('Searching…');
    setReplays([]);
    try {
      const data = await apiClient.searchReplays({
        userEmail: userEmail || undefined,
        // The fleet-wide index is partitioned by day, so a day is required
        // when there's no user to scope by.
        day: userEmail ? undefined : day,
      });
      const rows = data?.replays || [];
      // Errors first, then most recent. A replay with 40 errors in it is the
      // one to watch, and it is rarely the newest.
      rows.sort(
        (a, b) =>
          (b.errorCount || 0) - (a.errorCount || 0) ||
          String(b.startedAt || '').localeCompare(String(a.startedAt || '')),
      );
      setReplays(rows);
      setStatus(rows.length ? '' : 'No replays found.');
    } catch (e) {
      setStatus(`Search failed: ${e.message}`);
    }
  }, [apiClient, userEmail, day]);

  const open = useCallback((replay) => setOpenReplay(replay), []);

  const openByReplayId = useCallback(
    (event) => {
      event.preventDefault();
      const id = Number(replayId.trim());
      if (!Number.isSafeInteger(id) || id <= 0) {
        setStatus('Enter a valid replay ID.');
        return;
      }
      open({ replayId: id });
    },
    [open, replayId],
  );

  if (openReplay) {
    return <ReplayDayViewer key={`${openReplay.replayId}:${openReplay.timestamp ?? ''}`} apiClient={apiClient}
      initialReplay={openReplay} initialTimestamp={openReplay.timestamp ?? null} onClose={() => {
        setOpenReplay(null);
        const params = new URLSearchParams(location.search);
        params.delete('replayId');
        params.delete('at');
        navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
      }} />;
  }

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        <Card className="shadow mb-4">
          <CardHeader className="border-0">
            <h3 className="mb-0">Session Replays</h3>
            <small className="text-muted">
              Recordings of the EMR page and the Comprehend panel, with the
              network, message and console streams alongside them.
            </small>
          </CardHeader>
          <CardBody>
            <Form onSubmit={openByReplayId}>
              <Row>
                <Col md="5">
                  <FormGroup>
                    <Label>Replay ID</Label>
                    <Input
                      value={replayId}
                      placeholder="Paste the replay ID from the bug report"
                      onChange={(e) => setReplayId(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="3" className="d-flex align-items-end">
                  <FormGroup>
                    <Button color="primary" type="submit">
                      Open replay
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                search();
              }}
            >
              <Row>
                <Col md="5">
                  <FormGroup>
                    <Label>User email</Label>
                    <Input
                      value={userEmail}
                      placeholder="pt@clinic.com (blank = whole fleet)"
                      onChange={(e) => setUserEmail(e.target.value)}
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label>Day</Label>
                    <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
                  </FormGroup>
                </Col>
                <Col md="3" className="d-flex align-items-end">
                  <FormGroup>
                    <Button color="primary" type="submit">
                      Search
                    </Button>
                  </FormGroup>
                </Col>
              </Row>
            </Form>
            {status ? <div className="text-muted">{status}</div> : null}
          </CardBody>
        </Card>

        {replays.length ? (
          <Card className="shadow">
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th>Started</th>
                  <th>Duration</th>
                  <th>User</th>
                  <th>Kind</th>
                  <th>Errors</th>
                  <th>Events</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {replays.map((replay) => (
                  <tr key={replay.replayId}>
                    <td>{formatReplayTimestamp(replay.startedAt) || '—'}</td>
                    <td>{replayDuration(replay)}</td>
                    <td>{replay.userEmail || '—'}</td>
                    <td>{replay.kind || '—'}</td>
                    <td className={replay.errorCount ? 'text-danger' : ''}>{replay.errorCount || 0}</td>
                    <td>{replay.eventCount || 0}</td>
                    <td>
                      <Button size="sm" color="primary" onClick={() => open(replay)}>
                        Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        ) : null}
      </Container>
    </>
  );
};

/**
 * Exported wrapped: the gate is the only path to the viewer, and putting it here
 * rather than inside SessionReplayViewer means no future early-return in the
 * component body can accidentally render a replay ahead of it.
 */
const GatedSessionReplayViewer = () => (
  <ReplayAccessGate>
    <SessionReplayViewer />
  </ReplayAccessGate>
);

export default GatedSessionReplayViewer;
